import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useParams, useSearch } from 'wouter'
import queryString from 'query-string'
import { useShallow } from 'zustand/react/shallow'
import { useDocumentState, Clipboard } from '@/state'
import { isLine } from './components/Annotations/utils'
import {
  isKonvaAnnotationTemplate,
  type AnnotationToolPreset,
} from './components/Annotations/annotationSchema'
import { isLegacyLineBasedAnnotationType } from './components/Annotations/annotationMetadata'
import {
  listTemplateAttributesQuery,
  useBulkCreateTemplateAttributes,
  useBulkDeleteTemplateAttributes,
} from '@/services/queries/template_attributes'
import { createImage, TemplateAttribute, uploadFile } from '@/lib/api/client'
import { truncate } from '@/lib/truncate'
import { useDocumentPageQuery } from './queries'
import queryClient from '@/queryClient'
import QUERY_KEYS from '@/services/queries/images/queryKeys'
import deepEqual from 'deep-equal'
import { isMultiSelectModifierPressed } from './features/templateAttributesMultiSelect/isMultiSelectModifierPressed'

export const useDocumentPageParams = () => {
  const params = useParams<{
    projectId: string
    documentId: string
  }>()
  const search = useSearch()
  const queryParams = useMemo(() => {
    return queryString.parse(search) as {
      cv?: string // cad version
      dv?: string // document version
      comment_id?: string // comment id
    }
  }, [search])
  return { ...params, ...queryParams }
}

export const useComments = () => {
  const commentsEnabled = useDocumentState((state) => state.commentsEnabled)
  const { comment_id: commentId } = useDocumentPageParams()
  return commentsEnabled || !!commentId
}

const shouldIgnoreRecentKonvaAnnotationClick = () => {
  const lastKonvaAnnotationPointerDownAt = (
    window as Window & {
      __q20LastKonvaAnnotationPointerDownAt?: number
    }
  ).__q20LastKonvaAnnotationPointerDownAt

  return (
    typeof lastKonvaAnnotationPointerDownAt === 'number' &&
    Date.now() - lastKonvaAnnotationPointerDownAt < 500
  )
}

export const useDraggableElements = () => {
  const triggerLocation = useDocumentState((state) => state.triggerLocation)
  const { isLoading: isLoadingDocumentPageData, data: documentPageData } =
    useDocumentPageQuery()
  const clearNewElementData = useDocumentState(
    (state) => state.clearNewElementData,
  )
  const setLinePoint = useDocumentState((state) => state.setLinePoint)
  const newElementData = useDocumentState((state) => state.newElementData)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const selectedPage = useDocumentState((state) => state.currentPageInView)
  const canDropElement = useDocumentState((state) => state.canDropElement)
  const setCanDropElement = useDocumentState((state) => state.setCanDropElement)
  const zoom = useDocumentState((state) => state.zoom)
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const zoomPercentage = useMemo(() => zoom / 100, [zoom])
  const animationRef = useRef<number | null>(null)

  const bulkSelectedTemplateAttributes = useDocumentState(
    (s) => s.bulkSelectedTemplateAttributes,
  )
  const currentSubpageInView = useDocumentState((s) => s.currentSubpageInView)
  const setClipboard = useDocumentState((s) => s.setClipboard)
  const setDocumentState = useDocumentState(
    useShallow((state) => state.setState),
  )

  const { mutateAsync: createTemplates } = useBulkCreateTemplateAttributes()
  const { mutateAsync: deleteTemplates } = useBulkDeleteTemplateAttributes()
  const isAnyDialogOpen = useCallback(() => {
    /**
     * Invariant: while a modal dialog is open, document-level click handlers
     * should be inert so modal interactions do not affect page selection state.
     */
    return Boolean(document.querySelector('[role="dialog"]'))
  }, [])

  const alignPlaceholder = useCallback(
    (x: number, y: number) => {
      const dataType = newElementData?.dataType
      const type =
        dataType === 'annotation' ? newElementData?.attributes.type : dataType
      const iconPlaceholder = document.getElementById(
        `icon-placeholder--${type}`,
      ) as HTMLElement
      if (!iconPlaceholder) return
      const width = iconPlaceholder.offsetWidth * zoomPercentage
      const height = iconPlaceholder.offsetHeight * zoomPercentage

      iconPlaceholder.style.transform = `translate(${x - width / 2}px, ${
        y - height / 2
      }px) scale(${zoomPercentage})`
      iconPlaceholder.style.transformOrigin = '0 0'
    },
    [newElementData, zoomPercentage],
  )

  const calculateCanDrop = useCallback(
    ({ mousePosition }: { mousePosition: { x: number; y: number } }) => {
      const dataType = newElementData?.dataType
      const type =
        dataType === 'annotation' ? newElementData?.attributes.type : dataType
      const iconPlaceholder = document.getElementById(
        `icon-placeholder--${type}`,
      ) as HTMLElement
      const container = document.getElementById(
        `droppable-container--${selectedPage}`,
      ) as HTMLElement

      if (!container || !iconPlaceholder) return
      const {
        left: containerLeft,
        top: containerTop,
        right: containerRight,
        bottom: containerBottom,
      } = container.getBoundingClientRect()
      const { width: placeholderWidth, height: placeholderHeight } =
        iconPlaceholder.getBoundingClientRect()
      const preset = newElementData?.attributes.preset as
        | AnnotationToolPreset
        | undefined
      const isKonvaBoundsAnnotation =
        dataType === 'annotation' && preset?.defaultGeometry.mode === 'bounds'
      const widthOffset = !isLine(type || '')
        ? isKonvaBoundsAnnotation
          ? ((preset.defaultGeometry.width ??
              placeholderWidth / zoomPercentage) *
              zoomPercentage) /
            2
          : placeholderWidth / 2
        : 0
      const heightOffset = !isLine(type || '')
        ? isKonvaBoundsAnnotation
          ? ((preset.defaultGeometry.height ??
              placeholderHeight / zoomPercentage) *
              zoomPercentage) /
            2
          : placeholderHeight / 2
        : 0

      const canDrop =
        mousePosition.x - widthOffset >= containerLeft &&
        mousePosition.x + widthOffset <= containerRight &&
        mousePosition.y - heightOffset >= containerTop &&
        mousePosition.y + heightOffset <= containerBottom &&
        selectedPage === newElementData?.documentPageId

      if (canDrop !== canDropElement) setCanDropElement(canDrop)
    },
    [
      selectedPage,
      canDropElement,
      newElementData,
      setCanDropElement,
      zoomPercentage,
    ],
  )

  const onMouseMove = useCallback(
    (event: MouseEvent) => {
      alignPlaceholder(event.clientX, event.clientY)
      calculateCanDrop({
        mousePosition: { x: event.clientX, y: event.clientY },
      })
      if (newElementData?.attributes.startPoint) {
        if (animationRef.current) cancelAnimationFrame(animationRef.current)

        animationRef.current = requestAnimationFrame(() => {
          const container = document.getElementById(
            `droppable-container--${selectedPage}`,
          ) as HTMLElement
          const { x, y } = container.getBoundingClientRect()

          setLinePoint({
            type: 'end',
            position: {
              x: event.clientX / zoomPercentage - x / zoomPercentage,
              y: event.clientY / zoomPercentage - y / zoomPercentage,
            },
          })
        })
      }
    },
    [
      newElementData?.attributes.startPoint,
      selectedPage,
      zoomPercentage,
      setLinePoint,
      alignPlaceholder,
      calculateCanDrop,
    ],
  )

  const unselectElement = useCallback(
    (event: MouseEvent) => {
      if (isAnyDialogOpen()) return
      if (
        isMultiSelectModifierPressed(
          event,
          useDocumentState.getState().isSelectKeyPressed,
        )
      ) {
        return
      }

      const target = event.target
      if (!(target instanceof HTMLElement)) return

      if (
        target.closest('[role="menu"]') ||
        target.closest('[data-radix-context-menu-content]') ||
        target.closest('[data-radix-popper-content-wrapper]') ||
        target.closest('[data-konva-annotation-layer]')
      ) {
        return
      }
      if (shouldIgnoreRecentKonvaAnnotationClick()) {
        return
      }
      setSelectedElement(null)
    },
    [isAnyDialogOpen, setSelectedElement],
  )

  useEffect(() => {
    if (selectedElement) {
      document.addEventListener('click', unselectElement)
    } else {
      document.removeEventListener('click', unselectElement)
    }

    return () => {
      document.removeEventListener('click', unselectElement)
    }
  }, [unselectElement, selectedElement])

  useEffect(() => {
    const isEditable = (el: Element | null) => {
      if (!el) return false
      const tag = (el as HTMLElement).tagName
      const editable = (el as HTMLElement).isContentEditable
      return (
        editable ||
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        (tag === 'DIV' &&
          (el as HTMLElement).getAttribute('role') === 'textbox')
      )
    }

    const onClipboardKeydown = async (event: KeyboardEvent) => {
      const isMeta = event.metaKey
      const isCtrl = event.ctrlKey
      const key = event.key.toLowerCase()

      const active = document.activeElement
      if (isEditable(active)) return

      if ((isMeta || isCtrl) && (key === 'c' || key === 'x')) {
        if (!bulkSelectedTemplateAttributes?.length && !selectedElement) return

        event.preventDefault()

        const action = key === 'x' ? ('cut' as const) : ('copy' as const)

        let content: Array<TemplateAttribute> = bulkSelectedTemplateAttributes

        if (selectedElement && !bulkSelectedTemplateAttributes?.length) {
          const templates = await listTemplateAttributesQuery({
            documentPageId: selectedElement.documentPageId,
          })
          const selectedTemplate = templates.find(
            (t) => t.id === selectedElement.id,
          )
          if (selectedTemplate) {
            content = [selectedTemplate]
          }
        }

        const clipboard = {
          action,
          content,
          date: Date.now(),
          documentPage: selectedPage,
          documentSubpage: currentSubpageInView,
          origin: 'q20',
        }

        setClipboard(clipboard)

        if (action === 'cut') {
          void deleteTemplates({
            documentPageId: selectedPage as string,
            templateAttributes: content,
          })
        }

        void navigator.clipboard.writeText(JSON.stringify(clipboard, null, 2))
      }
    }

    document.addEventListener('keydown', onClipboardKeydown)
    return () => {
      document.removeEventListener('keydown', onClipboardKeydown)
    }
  }, [
    selectedElement,
    bulkSelectedTemplateAttributes,
    selectedPage,
    currentSubpageInView,
    setClipboard,
  ])

  useEffect(() => {
    if (isLoadingDocumentPageData) return
    const isEditable = (el: Element | null) => {
      if (!el) return false
      const tag = (el as HTMLElement).tagName
      const editable = (el as HTMLElement).isContentEditable
      return (
        editable ||
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        (tag === 'DIV' &&
          (el as HTMLElement).getAttribute('role') === 'textbox')
      )
    }

    const onClipboardPaste = async (event: ClipboardEvent) => {
      const active = document.activeElement
      if (isEditable(active)) return

      const dt = event.clipboardData
      if (!dt) return

      const documentVersionId = documentPageData?.documentVersion?.id
      const imageFiles: File[] = []

      const templates = await listTemplateAttributesQuery({
        documentPageId: selectedPage,
      })

      const getTemplatePosition = (item: TemplateAttribute) => {
        const templateValues = item.template_values as any

        if (
          item.data_type === 'annotation' &&
          isKonvaAnnotationTemplate(templateValues)
        ) {
          const konvaTemplateValues =
            templateValues as typeof templateValues & {
              position?: { z?: number }
            }

          return {
            x: konvaTemplateValues.geometry.x,
            y: konvaTemplateValues.geometry.y,
            z:
              konvaTemplateValues.geometry.z ?? konvaTemplateValues.position?.z,
          }
        }

        if (
          item.data_type === 'annotation' &&
          isLegacyLineBasedAnnotationType(templateValues?.type)
        ) {
          return {
            ...(templateValues?.startPoint || templateValues?.position || {}),
            z: templateValues?.position?.z,
          }
        }

        return {
          ...(templateValues?.position || {}),
        }
      }

      const zValues = templates?.map((t) => getTemplatePosition(t).z || 1000)
      const maxZIndex = zValues.length ? Math.max(...(zValues || [1000])) : 0

      const calculateNewPosition = (pos: { x: number; y: number }) => {
        const prevTemplatePos = templates.find((t) => {
          const tPos = getTemplatePosition(t)
          return deepEqual(
            {
              x: tPos.x,
              y: tPos.y,
            },
            {
              x: pos.x,
              y: pos.y,
            },
          )
        })

        return prevTemplatePos
          ? calculateNewPosition({
              x: pos.x + 20,
              y: pos.y + 20,
            })
          : {
              ...pos,
              z: maxZIndex + 1,
            }
      }

      const getPastedTemplateValues = ({
        item,
        newPosition,
        z,
      }: {
        item: TemplateAttribute
        newPosition: { x: number; y: number; z?: number }
        z: number
      }) => {
        const templateValues = item.template_values as any

        if (
          item.data_type === 'annotation' &&
          isKonvaAnnotationTemplate(templateValues)
        ) {
          const konvaTemplateValues =
            templateValues as typeof templateValues & {
              position?: { z?: number }
            }

          return {
            ...konvaTemplateValues,
            subPageId: currentSubpageInView,
            geometry: {
              ...konvaTemplateValues.geometry,
              x: newPosition.x,
              y: newPosition.y,
              z,
            },
            position: {
              ...(konvaTemplateValues.position || {}),
              z,
            },
          }
        }

        if (
          item.data_type === 'annotation' &&
          isLegacyLineBasedAnnotationType(templateValues?.type)
        ) {
          const originalPosition = getTemplatePosition(item)
          const dx = newPosition.x - originalPosition.x
          const dy = newPosition.y - originalPosition.y
          const shiftPosition = (position?: { x: number; y: number }) =>
            position
              ? {
                  ...position,
                  x: position.x + dx,
                  y: position.y + dy,
                }
              : position

          return {
            ...(item.template_values || {}),
            subPageId: currentSubpageInView,
            startPoint: shiftPosition(templateValues.startPoint),
            endPoint: shiftPosition(templateValues.endPoint),
            position: {
              ...(shiftPosition(
                templateValues.position || templateValues.startPoint,
              ) || {}),
              z,
            },
          }
        }

        return {
          ...(item.template_values || {}),
          subPageId: currentSubpageInView,
          position: {
            ...(item.template_values as any).position,
            ...{
              ...newPosition,
              z,
            },
          },
        }
      }

      if (dt.items && dt.items.length) {
        for (const item of Array.from(dt.items)) {
          if (
            item.kind === 'file' &&
            item.type &&
            item.type.startsWith('image/')
          ) {
            const f = item.getAsFile()
            if (f) imageFiles.push(f)
          }
        }
      }

      if (imageFiles.length === 0 && dt.files && dt.files.length) {
        for (const f of Array.from(dt.files)) {
          if (f.type && f.type.startsWith('image/')) {
            imageFiles.push(f)
          }
        }
      }

      if (imageFiles.length > 0) {
        event.preventDefault()

        const images = await Promise.all(
          imageFiles.map(async (file) => ({
            ...(await createImage(documentVersionId as string, {
              source_type: 'screenshot',
              name: truncate(file.name),
            })),
            file,
          })),
        )

        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.IMAGES, { documentVersionId }],
        })

        await createTemplates({
          documentPageId: selectedPage as string,
          payloads: images.map((img, i) => {
            const newPosition = calculateNewPosition({
              x: 280 + i * 20,
              y: 180 + i * 20,
            })
            return {
              data_type: 'image',
              template_values: {
                subPageId: currentSubpageInView,
                imageId: img.id,
                imagePosition: 'absolute',
                position: {
                  ...newPosition,
                  z: maxZIndex + 1 + i,
                },
              } as any,
            }
          }),
        })

        await Promise.all(
          images.map((imageData) =>
            uploadFile(imageData.upload_url as string, imageData.file),
          ),
        )

        setDocumentState({
          bulkSelectedTemplateAttributes: [],
          selectedElement: null,
        })
        return
      }

      const text = dt.getData('text/plain') || ''

      let clip: Clipboard | null = null
      try {
        clip = JSON.parse(text)
      } catch {
        return
      }

      if (!clip || clip.origin !== 'q20') return

      event.preventDefault()

      try {
        setClipboard(clip)
      } catch {
        // Ignore
      }

      if (!selectedPage || !currentSubpageInView) return

      const pasted = (clip.content || []).map((item: TemplateAttribute, i) => {
        const newPos = calculateNewPosition(getTemplatePosition(item))
        const z = (newPos.z || 0) + i

        return {
          data_type: item.data_type,
          template_values: getPastedTemplateValues({
            item,
            newPosition: newPos,
            z,
          }),
        }
      })

      if (pasted.length === 0) return

      try {
        await createTemplates({
          documentPageId: selectedPage,
          payloads: pasted as any,
        })
        setDocumentState({
          bulkSelectedTemplateAttributes: [],
          selectedElement: null,
        })
      } catch (e) {
        console.error('Failed to create templates from paste', e)
        return
      }

      if (clip.action === 'cut') {
        setClipboard(null)
      }
    }

    document.addEventListener('paste', onClipboardPaste)
    return () => {
      document.removeEventListener('paste', onClipboardPaste)
    }
  }, [
    selectedPage,
    currentSubpageInView,
    isLoadingDocumentPageData,
    documentPageData,
    createTemplates,
    deleteTemplates,
    setClipboard,
  ])

  const onStopAddingNewElement = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearNewElementData()
    },
    [clearNewElementData],
  )

  const onUnselectElements = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setDocumentState({
          bulkSelectedTemplateAttributes: [],
          selectedElement: null,
        })

        setTimeout(() => {
          ;(document.activeElement as HTMLElement | null)?.blur()
        })
      }
    },
    [setDocumentState],
  )

  const addNewElementListeners = useCallback(() => {
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('keydown', onStopAddingNewElement)
  }, [onMouseMove, onStopAddingNewElement])

  const removeNewElementListeners = useCallback(() => {
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('keydown', onStopAddingNewElement)
    if (animationRef.current) cancelAnimationFrame(animationRef.current)
  }, [onMouseMove, onStopAddingNewElement])

  useEffect(() => {
    if (isAddingElement && triggerLocation) {
      addNewElementListeners()
    } else if (!isAddingElement) {
      removeNewElementListeners()
    }

    return () => removeNewElementListeners()
  }, [
    isAddingElement,
    triggerLocation,
    addNewElementListeners,
    removeNewElementListeners,
  ])

  useEffect(() => {
    if (isAddingElement && triggerLocation) {
      alignPlaceholder(triggerLocation.x, triggerLocation.y)
    }
  }, [alignPlaceholder, isAddingElement, triggerLocation])

  useEffect(() => {
    if (selectedElement || bulkSelectedTemplateAttributes.length) {
      document.addEventListener('keydown', onUnselectElements)
    } else {
      document.removeEventListener('keydown', onUnselectElements)
    }

    return () => document.removeEventListener('keydown', onUnselectElements)
  }, [selectedElement, bulkSelectedTemplateAttributes, onUnselectElements])
}
